import React, { useEffect, useState } from "react";
import { X, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Premium fullscreen image lightbox for the product detail page.
 *
 * - Click outside the image, the X button, or press Esc → close
 * - Click the image inside → toggle zoom (1x ↔ 2.25x with mouse-position panning)
 * - On mobile: native pinch-to-zoom is enabled via touch-action: pinch-zoom
 * - Body scroll is locked while open
 */
export const ImageLightbox = ({ src, alt = "", open, onClose }) => {
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });

  useEffect(() => {
    if (!open) return;
    setZoomed(false);
    setOrigin({ x: 50, y: 50 });
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !src) return null;

  const onImageClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setOrigin({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
    setZoomed((z) => !z);
  };

  const onMouseMove = (e) => {
    if (!zoomed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setOrigin({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <div
      data-testid="lightbox-overlay"
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 animate-[fadeUp_0.25s_ease-out_both]"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        data-testid="lightbox-close"
        aria-label="Chiudi"
        className="absolute top-5 right-5 md:top-8 md:right-8 z-10 bg-white/10 hover:bg-white/20 text-white p-3 transition-colors border border-white/15"
      >
        <X strokeWidth={1.25} className="w-5 h-5" />
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setZoomed((z) => !z);
        }}
        data-testid="lightbox-zoom-toggle"
        aria-label={zoomed ? "Riduci" : "Ingrandisci"}
        className="absolute top-5 right-20 md:top-8 md:right-24 z-10 bg-white/10 hover:bg-white/20 text-white p-3 transition-colors border border-white/15"
      >
        {zoomed ? <ZoomOut strokeWidth={1.25} className="w-5 h-5" /> : <ZoomIn strokeWidth={1.25} className="w-5 h-5" />}
      </button>

      <div
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ touchAction: zoomed ? "pan-x pan-y" : "pinch-zoom" }}
      >
        <img
          src={src}
          alt={alt}
          data-testid="lightbox-image"
          onClick={onImageClick}
          onMouseMove={onMouseMove}
          draggable={false}
          style={{
            transform: zoomed ? "scale(2.25)" : "scale(1)",
            transformOrigin: `${origin.x}% ${origin.y}%`,
            cursor: zoomed ? "zoom-out" : "zoom-in",
            transition: "transform 350ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
          className="max-w-[92%] max-h-[92%] md:max-w-[88%] md:max-h-[88%] w-auto h-auto object-contain select-none"
        />
      </div>

      <div className="absolute bottom-6 left-0 right-0 text-center uppercase tracking-[0.32em] text-[10px] text-white/50">
        {zoomed ? "Clicca per ridurre · Esc per chiudere" : "Clicca per ingrandire · Esc per chiudere"}
      </div>
    </div>
  );
};

export default ImageLightbox;
